import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { SURVEY_STATUS } from 'services/api/SurveyApi/data';
import { actions as surveyActions } from 'features/survey';

export const SURVEY_SCREENS = {
  INTRO: 'intro',
  QUESTION: 'question',
  THANKS: 'thanks',
};

const useSurvey = () => {
  const dispatch = useDispatch();
  const locale = useSelector(state => state.locale.locale);
  const { isAuth } = useSelector(state => state.auth, shallowEqual);
  const { status, questions, currentQuestionId, isModalOpen, isStatusLoaded, actionProcessing } = useSelector(
    state => state.survey,
    shallowEqual,
  );

  const [isStarted, setIsStarted] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (isAuth && !isStatusLoaded) {
      dispatch(surveyActions.getSurveyStatus());
    }
  }, [isAuth, isStatusLoaded, dispatch]);

  useEffect(() => {
    setSelected([]);
  }, [currentQuestionId]);

  const screen = useMemo(() => {
    if (status === SURVEY_STATUS.COMPLETED) return SURVEY_SCREENS.THANKS;
    if (isStarted || status === SURVEY_STATUS.STARTED) return SURVEY_SCREENS.QUESTION;

    return SURVEY_SCREENS.INTRO;
  }, [isStarted, status]);

  const questionIndex = useMemo(
    () => questions.findIndex(item => item.id === currentQuestionId),
    [questions, currentQuestionId],
  );

  const question = questionIndex === -1 ? null : questions[questionIndex];

  const toggleChoice = useCallback(choiceId => {
    setSelected(prev => (prev.includes(choiceId) ? prev.filter(item => item !== choiceId) : [...prev, choiceId]));
  }, []);

  const handleStart = useCallback(() => {
    setIsStarted(true);
  }, []);

  const handleNext = useCallback(() => {
    if (!question || !selected.length || actionProcessing) return;

    dispatch(surveyActions.submitSurveyAnswer(question.id, selected));
  }, [dispatch, question, selected, actionProcessing]);

  const handleClose = useCallback(() => {
    dispatch(surveyActions.closeSurveyModal());
  }, [dispatch]);

  return {
    locale,
    isModalOpen,
    screen,
    question,
    questionNumber: questionIndex + 1,
    questionsCount: questions.length,
    selected,
    actionProcessing,
    toggleChoice,
    handleStart,
    handleNext,
    handleClose,
  };
};

export default useSurvey;
